<template>
    <v-container fluid class="pa-8 bg-grey-lighten-5 h-100 overflow-y-auto">
        <!-- Header Section -->
        <div class="d-flex justify-space-between align-end mb-10 header-animate">
            <div>
                <div class="d-flex align-center mb-2">
                    <v-icon color="indigo-accent-4" size="32" class="mr-3">mdi-file-tree</v-icon>
                    <h2 class="text-h3 font-weight-black text-indigo-darken-4">분류 마스터</h2>
                </div>
                <p class="text-subtitle-1 text-grey-darken-1">데이터의 구조를 정의하고 계층별 카테고리를 직관적으로 관리하세요.</p>
            </div>
            <v-btn 
                color="indigo-darken-2" 
                prepend-icon="mdi-plus-thick" 
                class="px-8 rounded-xl font-weight-black add-btn-main shadow-glow-indigo"
                height="56"
                @click="openAddDialog(1, null)"
            >
                새 대분류 추가
            </v-btn>
        </div>

        <!-- Main Tree Section -->
        <div class="white-container pa-1">

            <div class="tree-body pb-6">
                <transition-group name="list" tag="div">
                    <template v-for="large in categoryTree" :key="large.catId">
                        <!-- L1: Large Category Card -->
                        <div class="tree-node-l1 mb-4 mx-4">
                            <div class="node-content l1-content px-6 py-4 d-flex align-center" :class="{ 'active-node': large.expanded }">
                                <div class="d-flex align-center" style="width: 75%">
                                    <v-btn
                                        variant="tonal"
                                        density="comfortable"
                                        size="small"
                                        :icon="large.expanded ? 'mdi-minus' : 'mdi-plus'"
                                        @click="large.expanded = !large.expanded"
                                        class="mr-4 expand-btn"
                                        :color="large.expanded ? 'indigo-accent-4' : 'indigo-lighten-2'"
                                    ></v-btn>
                                    <div class="icon-box l1-icon mr-4">
                                        <v-icon color="white">mdi-folder-network</v-icon>
                                    </div>
                                    <div>
                                        <div class="text-h6 font-weight-bold text-indigo-darken-4">{{ large.catNm }}</div>
                                    </div>
                                </div>
                                <div class="d-flex justify-center align-center actions-box" style="width: 25%">
                                    <v-btn icon="mdi-plus" variant="text" color="indigo-accent-2" class="mr-1 hover-scale" @click="openAddDialog(2, large.catId)" title="중분류 추가"></v-btn>
                                    <v-btn icon="mdi-pencil-outline" variant="text" color="indigo-lighten-2" class="mr-1 hover-scale" @click="openEditDialog(large)"></v-btn>
                                    <v-btn icon="mdi-trash-can-outline" variant="text" color="red-lighten-2" class="hover-scale" @click="confirmDelete(large)"></v-btn>
                                </div>
                            </div>

                            <!-- L2: Middle Categories -->
                            <v-expand-transition>
                                <div v-if="large.expanded" class="node-children pl-10 pt-2">
                                    <template v-for="(middle, mIdx) in large.children" :key="middle.catId">
                                        <div class="tree-node-l2 mb-2 relative">
                                            <div class="tree-line-v" v-if="mIdx < large.children.length - 1"></div>
                                            <div class="tree-line-h"></div>
                                            
                                            <div class="node-content l2-content px-5 py-3 d-flex align-center" :class="{ 'active-node': middle.expanded }">
                                                <div class="d-flex align-center" style="width: 75%">
                                                    <v-btn
                                                        variant="text"
                                                        density="compact"
                                                        size="small"
                                                        :icon="middle.expanded ? 'mdi-chevron-down' : 'mdi-chevron-right'"
                                                        @click="middle.expanded = !middle.expanded"
                                                        class="mr-3"
                                                        color="blue-darken-2"
                                                    ></v-btn>
                                                    <v-icon color="blue-lighten-1" class="mr-3">mdi-folder-open</v-icon>
                                                    <span class="text-subtitle-1 font-weight-bold text-blue-darken-4">{{ middle.catNm }}</span>
                                                </div>
                                                <div class="d-flex justify-center align-center actions-box" style="width: 25%">
                                                    <v-btn icon="mdi-plus" variant="text" color="teal-accent-4" size="small" @click="openAddDialog(3, middle.catId)" title="소분류 추가"></v-btn>
                                                    <v-btn icon="mdi-pencil-outline" variant="text" color="grey" size="small" @click="openEditDialog(middle)"></v-btn>
                                                    <v-btn icon="mdi-trash-can-outline" variant="text" color="red-lighten-3" size="small" @click="confirmDelete(middle)"></v-btn>
                                                </div>
                                            </div>

                                            <!-- L3: Small Categories -->
                                            <v-expand-transition>
                                                <div v-if="middle.expanded" class="node-children pl-12 pt-1 pb-2">
                                                    <template v-for="(small, sIdx) in middle.children" :key="small.catId">
                                                        <div class="tree-node-l3 mb-1 relative">
                                                            <div class="tree-line-v-small" v-if="sIdx < middle.children.length - 1"></div>
                                                            <div class="tree-line-h-small"></div>

                                                            <div class="node-content l3-content px-4 py-2 d-flex align-center">
                                                                <div class="d-flex align-center" style="width: 75%">
                                                                    <div class="l3-dot mr-3"></div>
                                                                    <span class="text-body-2 font-weight-medium text-teal-darken-3">{{ small.catNm }}</span>
                                                                </div>
                                                                <div class="d-flex justify-center align-center actions-box" style="width: 25%">
                                                                    <v-btn icon="mdi-pencil-outline" variant="text" color="grey-lighten-1" size="x-small" @click="openEditDialog(small)"></v-btn>
                                                                    <v-btn icon="mdi-trash-can-outline" variant="text" color="red-lighten-4" size="x-small" @click="confirmDelete(small)"></v-btn>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </template>
                                                </div>
                                            </v-expand-transition>
                                        </div>
                                    </template>
                                </div>
                            </v-expand-transition>
                        </div>
                    </template>
                </transition-group>

                <div v-if="categoryTree.length === 0" class="empty-state pa-12 text-center">
                    <v-img :src="require('@/assets/images/Subtract.png')" width="100" class="mx-auto mb-4 opacity-10 filter-grayscale-dark"></v-img>
                    <div class="text-h6 text-grey-lighten-1">표시할 데이터가 없습니다.</div>
                    <p class="text-grey-lighten-2">우측 상단의 '새 대분류 추가' 버튼을 눌러 시작하세요.</p>
                </div>
            </div>
        </div>

        <!-- Styled Dialog -->
        <v-dialog v-model="dialog" max-width="480px" persistent transition="dialog-bottom-transition">
            <v-card class="rounded-pill-custom pb-4">
                <div class="dialog-header pa-8 pb-4 text-center">
                    <div class="dialog-icon-box mx-auto mb-4" :class="'lv' + editedItem.catLvl">
                        <v-icon color="white" size="32">{{ getLevelIcon(editedItem.catLvl) }}</v-icon>
                    </div>
                    <div class="text-h5 font-weight-black text-indigo-darken-4">
                        {{ isEditMode ? '분류 정보 수정' : '새로운 ' + getLevelName(editedItem.catLvl) + ' 추가' }}
                    </div>
                    <div class="text-caption text-indigo-lighten-2 mt-1" v-if="!isEditMode && editedItem.upperCatId">
                        상위 분류 하위에 귀속됩니다.
                    </div>
                </div>

                <v-card-text class="px-8 py-2">
                    <v-text-field
                        v-model="editedItem.catNm"
                        label="분류 명칭"
                        variant="filled"
                        rounded="xl"
                        color="indigo-accent-4"
                        bg-color="indigo-lighten-5"
                        placeholder="예: IT/기술, 인사관리 등"
                        @keyup.enter="saveItem"
                        autofocus
                        class="custom-field"
                    ></v-text-field>
                </v-card-text>

                <v-card-actions class="px-8 pt-4 pb-4">
                    <v-btn variant="text" color="grey-darken-1" @click="closeDialog" class="rounded-xl px-6">취소하기</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn 
                        color="indigo-accent-4" 
                        variant="flat" 
                        @click="saveItem" 
                        class="rounded-xl px-10 font-weight-black shadow-glow-sm"
                        height="48"
                    >
                        {{ isEditMode ? '변경사항 저장' : '지금 추가하기' }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script setup>
import { ref, onMounted, reactive, computed, getCurrentInstance } from 'vue';

const { proxy } = getCurrentInstance();
const axios = proxy.$axios;

const categoryTree = ref([]);
const dialog = ref(false);
const editedIndex = ref(-1);
const editedItem = reactive({
    catId: null,
    catNm: '',
    catLvl: null,
    upperCatId: null
});

const isEditMode = computed(() => editedIndex.value > -1);

const getLevelName = (lvl) => {
    if (lvl === 1) return '대분류';
    if (lvl === 2) return '중분류';
    if (lvl === 3) return '소분류';
    return '';
};

const getLevelIcon = (lvl) => {
    if (lvl === 1) return 'mdi-folder-network';
    if (lvl === 2) return 'mdi-folder-open';
    if (lvl === 3) return 'mdi-tag-outline';
    return 'mdi-folder';
};

const fetchAllCategories = async () => {
    try {
        const res = await axios.post('/rag/category/list', {});
        const allList = res.data.data.list;

        const tree = [];
        const map = {};

        allList.forEach(item => {
            map[item.catId] = { ...item, children: [], expanded: true };
        });

        allList.forEach(item => {
            if (item.upperCatId && map[item.upperCatId]) {
                map[item.upperCatId].children.push(map[item.catId]);
            } else if (item.catLvl === 1) {
                tree.push(map[item.catId]);
            }
        });

        const updateTreeWithState = (newTree, oldTree) => {
            newTree.forEach(newNode => {
                const oldNode = oldTree.find(o => o.catId === newNode.catId);
                if (oldNode) {
                    newNode.expanded = oldNode.expanded;
                    if (newNode.children.length > 0 && oldNode.children.length > 0) {
                        updateTreeWithState(newNode.children, oldNode.children);
                    }
                }
            });
        };

        if (categoryTree.value.length > 0) {
            updateTreeWithState(tree, categoryTree.value);
        }

        categoryTree.value = tree;
    } catch (err) {
        console.error('Data loading error:', err);
    }
};

const openAddDialog = (lvl, upperId) => {
    editedIndex.value = -1;
    editedItem.catId = null;
    editedItem.catNm = '';
    editedItem.catLvl = lvl;
    editedItem.upperCatId = upperId;
    dialog.value = true;
};

const openEditDialog = (item) => {
    editedIndex.value = 1;
    editedItem.catId = item.catId;
    editedItem.catNm = item.catNm;
    editedItem.catLvl = item.catLvl;
    editedItem.upperCatId = item.upperCatId;
    dialog.value = true;
};

const closeDialog = () => {
    dialog.value = false;
};

const saveItem = async () => {
    if (!editedItem.catNm) return;
    try {
        const res = await axios.post('/rag/category/save', editedItem);
        if (res.data.status === 'success') {
            closeDialog();
            fetchAllCategories();
        }
    } catch (err) {
        console.error(err);
    }
};

const confirmDelete = async (item) => {
    if (confirm(`정말 '${item.catNm}'을(를) 삭제하시겠습니까?`)) {
        try {
            await axios.post('/rag/category/delete', { catId: item.catId });
            fetchAllCategories();
        } catch (err) {
            console.error(err);
        }
    }
};

onMounted(() => {
    fetchAllCategories();
});
</script>

<style scoped>
/* Background & Main Container */
.bg-grey-lighten-5 {
    background-color: #f8f9fa !important;
}

/* Header & Text */
.header-animate {
    animation: fadeInDown 0.8s cubic-bezier(0.22, 1, 0.36, 1);
}

/* White Container (Modern List Card) */
.white-container {
    background: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.05);
    border-radius: 32px;
    box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.05);
    min-height: 600px;
}

.tree-header {
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

/* Node Styles - Level 1 (Large) */
.tree-node-l1 {
    perspective: 1000px;
}

.l1-content {
    background: #ffffff;
    border: 1px solid rgba(61, 90, 254, 0.1);
    border-radius: 20px;
    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    box-shadow: 0 4px 12px rgba(0,0,0,0.03);
}

.l1-content:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 24px rgba(61, 90, 254, 0.1);
    border-color: rgba(61, 90, 254, 0.3);
}

.l1-content.active-node {
    border-left: 6px solid #3d5afe;
    background: linear-gradient(to right, #f8faff, #ffffff);
}

.icon-box {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.l1-icon {
    background: linear-gradient(135deg, #3d5afe 0%, #1a237e 100%);
}

/* Node Styles - Level 2 (Middle) */
.l2-content {
    background: #f1f8ff;
    border-radius: 16px;
    border: 1px solid rgba(33, 150, 243, 0.1);
    transition: all 0.3s ease;
}

.l2-content:hover {
    background: #ffffff;
    border-color: rgba(33, 150, 243, 0.3);
    box-shadow: 0 6px 12px rgba(33, 150, 243, 0.08);
}

.relative { position: relative; }

/* Tree Lines - Slightly stronger for better awareness */
.tree-line-v {
    position: absolute;
    left: -24px;
    top: 30px;
    bottom: -15px;
    width: 2px;
    background: rgba(61, 90, 254, 0.15);
}

.tree-line-h {
    position: absolute;
    left: -24px;
    top: 25px;
    width: 20px;
    height: 2px;
    background: rgba(61, 90, 254, 0.15);
}

.tree-line-v-small {
    position: absolute;
    left: -20px;
    top: 20px;
    bottom: -10px;
    width: 1px;
    background: rgba(0, 0, 0, 0.03);
}

.tree-line-h-small {
    position: absolute;
    left: -20px;
    top: 18px;
    width: 15px;
    height: 1px;
    background: rgba(0, 0, 0, 0.03);
}

/* Node Styles - Level 3 (Small) */
.l3-content {
    background: #f2fdfb;
    border-radius: 12px;
    border: 1px solid rgba(0, 191, 165, 0.08);
}

.l3-content:hover {
    background: #ffffff;
    border-color: rgba(0, 191, 165, 0.3);
}

.l3-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #00897b;
}

/* Buttons & Chips */
.shadow-glow-indigo { box-shadow: 0 8px 15px rgba(61, 90, 254, 0.2); }
.shadow-glow-indigo:hover { box-shadow: 0 12px 25px rgba(61, 90, 254, 0.4); transform: scale(1.05); }

.expand-btn { transition: transform 0.3s ease; }
.expand-btn:hover { background: rgba(0,0,0,0.05); }

/* Transitions */
.list-enter-active, .list-leave-active { transition: all 0.5s ease; }
.list-enter-from { opacity: 0; transform: translateX(-30px); }
.list-leave-to { opacity: 0; transform: scale(0.9); }

/* Dialog Styles */
.rounded-pill-custom {
    border-radius: 40px !important;
    overflow: hidden;
    background: white !important;
}

.dialog-icon-box {
    width: 70px;
    height: 70px;
    border-radius: 25px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 10px 20px rgba(0,0,0,0.1);
}

.lv1 { background: linear-gradient(135deg, #3d5afe, #1a237e); }
.lv2 { background: linear-gradient(135deg, #2196f3, #0d47a1); }
.lv3 { background: linear-gradient(135deg, #00bfa5, #004d40); }

.shadow-glow-sm { box-shadow: 0 8px 15px rgba(61, 90, 254, 0.3); }

/* Animations */
@keyframes fadeInDown {
    from { opacity: 0; transform: translateY(-30px); }
    to { opacity: 1; transform: translateY(0); }
}

/* Scrollbar */
::-webkit-scrollbar { width: 6px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.05); border-radius: 10px; }
::-webkit-scrollbar-thumb:hover { background: rgba(0,0,0,0.1); }

.filter-grayscale-dark { filter: grayscale(1) opacity(0.3); }
.hover-scale:hover { transform: scale(1.2); transition: 0.2s ease; }
</style>
